// ---------------------------------------------------------------------------
// AppLock — AES-256-GCM encryption / decryption
// ---------------------------------------------------------------------------
import { toBase64, fromBase64 } from '../auth/key-derivation.js';
const IV_LENGTH = 12; // 96 bits, recommended for AES-GCM
const CURRENT_VERSION = 1;
/**
 * Encrypt a plaintext string with AES-256-GCM.
 *
 * - Generates a fresh random IV per call (critical for GCM security).
 * - GCM provides authenticated encryption: tampered ciphertext will
 *   fail decryption rather than producing garbled plaintext.
 */
export async function encrypt(key, plaintext) {
    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
    const encoded = new TextEncoder().encode(plaintext);
    const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);
    return {
        v: CURRENT_VERSION,
        iv: toBase64(iv),
        ct: toBase64(ciphertext),
    };
}
/**
 * Decrypt an AES-256-GCM encrypted payload.
 *
 * Throws `AppLockTamperError` if the ciphertext has been modified
 * (GCM authentication tag mismatch).
 */
export async function decrypt(key, payload) {
    if (payload.v !== CURRENT_VERSION) {
        throw new AppLockCryptoError('unsupported_version', `Unsupported encryption format version: ${payload.v}`);
    }
    const iv = fromBase64(payload.iv);
    const ciphertext = fromBase64(payload.ct);
    try {
        const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: iv }, key, ciphertext);
        return new TextDecoder().decode(decrypted);
    }
    catch (err) {
        // AES-GCM throws a generic DOMException on auth tag mismatch
        throw new AppLockCryptoError('tamper', 'Decryption failed — data may have been tampered with or the key is incorrect.');
    }
}
/**
 * Check if a raw string from storage looks like an encrypted payload.
 */
export function isEncryptedPayload(raw) {
    try {
        const parsed = JSON.parse(raw);
        return (typeof parsed === 'object' &&
            parsed !== null &&
            typeof parsed.v === 'number' &&
            typeof parsed.iv === 'string' &&
            typeof parsed.ct === 'string');
    }
    catch {
        return false;
    }
}
// ---------------------------------------------------------------------------
// Error types
// ---------------------------------------------------------------------------
export class AppLockCryptoError extends Error {
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'AppLockCryptoError';
    }
}
//# sourceMappingURL=crypto-adapter.js.map